import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Upload, X, Check, CheckCircle } from "lucide-react";
import { useFileUpload } from "@/hooks/useFileUpload";
import { CandidateService } from "@/services/candidateService";
import { JobService } from "@/services/jobService";
import { loadDefaultCurrency, getDefaultCurrencyFallback } from "@/hooks/useDefaultCurrency";
import { cn } from "@/lib/utils";
import { MAX_FILE_UPLOAD } from "@/config/constants";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader } from "@/components/ui/Card";
import toast from "react-hot-toast";
import FilePreview from "@/components/shared/FilePreview";
import ProgressStatus from "@/components/shared/ProgressStatus";
import ResumeValidationModal from "@/components/shared/ResumeValidationModal";
import ReviewWizard, { isDeeplyEmpty, STEP_CONFIG } from "../confirm-details/components/ReviewWizard";
import { defaultValues, useReviewForm } from "../confirm-details/hooks/useReviewForm";
import { mapApiToForm } from "../confirm-details/utils/mapApiToForm";
import { calculateTotalExperience } from "../confirm-details/utils/calculateExperience";
import LanguageProficiencyRows from "@/components/shared/LanguageProficiencyRows";
import { requiredLanguageNames } from "@/utils/languageRequirements";
import { buildQuestionAnswers } from "@/utils/screeningAnswers";

const BC_CHANNEL = "candidate_sync";

let _cachedFile = null;
let _cachedConsent = false;
// Module-level so the "this exact file already passed validation (or Continue
// Anyway was chosen)" decision survives the confirm-details round trip, same
// as _cachedFile — otherwise a file that was already cleared would be
// re-validated and re-flagged after the candidate comes back, showing the
// warning a second time.
let _approvedFile = null;

// Review wizard step key → the backend section(s) that fill it. A step is
// shown (editable) once ANY of its sections arrives; it only turns green/red
// once ALL of them have (until then it stays amber). "salary" has no section —
// total experience is derived from all work data, so it waits for `complete`.
const STEP_SECTIONS = {
  personal_information: ["personal_information", "summary"],
  skills: ["skills", "languages"],
  experience: ["work_experience"],
  education: ["education"],
  salary: [],
  more: ["additional_sections"],
};

const ALL_SECTIONS = ["personal_information", "summary", "skills", "languages", "work_experience", "education", "additional_sections"];

const UserApply = () => {
  const location = useLocation();
  const isBackFromConfirm = location.state?.fromConfirm === true;
  const jobTitle = location.state?.jobTitle ?? "";
  const [consentText, setConsentText] = useState("");
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);


  const { jobId } = useParams();
  const navigate = useNavigate();

  const {
    fileInputRef,
    isDragging,
    selectedFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    triggerFileInput,
    handleFileSelect,
    resetUpload,
    setSelectedFile,
  } = useFileUpload(MAX_FILE_UPLOAD, [".pdf", ".doc", ".docx"]);

  const [hasConsented, setHasConsented] = useState(false);
  // Wording only — tells the candidate up front whether a confirmation email
  // is coming. `null` until known (or if the read fails), in which case we say
  // nothing rather than promise the wrong thing. The submit response, not this,
  // decides what the success screen shows.
  // Set from the job this page already fetches below, not from a tenant-wide
  // read: the value is per job now, and an org default of "on" would have
  // promised an email on a job whose recruiter turned the step off.
  const [applyEmailConsentRequired, setApplyEmailConsentRequired] = useState(null);
  // Manual screening questions for this job (empty when none / not entitled).
  const [screeningQuestions, setScreeningQuestions] = useState([]);

  useEffect(() => {
    const fetchConsentText = async () => {
      try {
        const response = await CandidateService.getConsentText();

        setConsentText(
          response?.data?.consent_text ||
          response?.consent_text ||
          response?.text ||
          ""
        );
      } catch (error) {
        console.error("Failed to fetch consent text:", error);
      }
    };

    fetchConsentText();
  }, []);

  useEffect(() => {
    if (isBackFromConfirm && _cachedFile && !selectedFile) {
      setSelectedFile(_cachedFile);
      setHasConsented(_cachedConsent);
    } else if (!isBackFromConfirm) {
      _cachedFile = null;
      _cachedConsent = false;
      _approvedFile = null;
    }
  }, []);

  useEffect(() => {
    _cachedFile = selectedFile;
  }, [selectedFile]);

  useEffect(() => {
    _cachedConsent = hasConsented;
  }, [hasConsented]);

  const abortControllerRef = useRef(null);
  // Offset into the cumulative SSE responseText that we've already parsed.
  const consumedRef = useRef(0);
  // Accumulates the raw backend draft as sections stream in; shaped like the
  // /candidate-review response so mapApiToForm can consume it directly.
  const draftRef = useRef({ draft_data: {}, total_experience: undefined });

  // "upload" → file/consent form; "review" → inline streaming review wizard.
  const [phase, setPhase] = useState("upload");
  const [sseProgress, setSseProgress] = useState({ step: 0, total: 5, percentage: 0, message: "" });
  const [sseError, setSseError] = useState("");
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  // True while the resume is streaming but we haven't yet confirmed the
  // candidate isn't a duplicate. We stay on the upload page (showing progress)
  // until the first real section/complete arrives, so an "already applied"
  // error never surfaces inside the review wizard.
  const [isExtracting, setIsExtracting] = useState(false);

  const [mandatoryFields, setMandatoryFields] = useState([]);
  const [readySections, setReadySections] = useState([]);
  const [extractionComplete, setExtractionComplete] = useState(false);
  const [extractionQuality, setExtractionQuality] = useState("full");
  const [candidateId, setCandidateId] = useState("");
  const [applicationId, setApplicationId] = useState("");
  const [isFormSubmit, setIsFormSubmit] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [portalUnavailable, setPortalUnavailable] = useState(false);
  // The job's field config / detail could not be read. Blocks the page: this
  // is where the screening questions come from, and a form missing them is a
  // form the server will refuse.
  const [jobLoadFailed, setJobLoadFailed] = useState(false);

  const [currencySign, setCurrencySign] = useState(getDefaultCurrencyFallback().currencySign);
  const [jobLanguagesRequired, setJobLanguagesRequired] = useState([]);
  // The rows the job asks about: prefilled by mapApiToForm, locked in the
  // editor, and refused blank by the review schema. One derivation for all
  // three.
  const lockedLanguages = useMemo(
    () => requiredLanguageNames(jobLanguagesRequired),
    [jobLanguagesRequired],
  );

  // Fetch mandatory-field config up front so the review wizard validates correctly.
  // Also doubles as the "is this tenant's portal still active" check for a
  // candidate who deep-links straight to /user/apply/:jobId without going
  // through job-listing/job-detail first.
  useEffect(() => {
    if (!jobId) return;

    (async () => {
      try {
        const [configRes, jobRes] = await Promise.all([
          JobService.fetchPublicJobFieldConfig(jobId),
          JobService.fetJobDetails(jobId),
          loadDefaultCurrency(),
        ]);

        if (configRes?.success && configRes?.data) {
          const allMandatory = [
            ...(configRes.data.always_mandatory || []).map((f) => f.key),
            ...(configRes.data.configured_mandatory || []).map((f) => f.key),
          ];

          setMandatoryFields(allMandatory);
        }

        if (jobRes?.data && jobRes.data.currency_sign !== undefined) {
          setCurrencySign(jobRes.data.currency_sign || "");
        }

        // The job's own required list, if any — the locked rows.
        setJobLanguagesRequired(jobRes?.data?.languages_required ?? []);
        // The `typeof` guard keeps the tri-state honest: this job read is the
        // one call in the batch that swallows its own failure above, so a
        // missing key has to leave the flag `null` and the wording silent
        // rather than default either way.
        const consentFlag = jobRes?.data?.require_apply_email_consent;
        if (typeof consentFlag === "boolean") {
          setApplyEmailConsentRequired(consentFlag);
        }
        setScreeningQuestions(jobRes?.data?.application_questions || []);
      } catch (err) {
        // Tenant deactivated/deleted — backend blocks the whole public portal
        // (see server/app/core/tenant_db.py get_public_db).
        if (err?.response?.data?.error_code === "PORTAL_UNAVAILABLE") {
          setPortalUnavailable(true);
        } else {
          // Loudly, on purpose. The job detail carries this job's screening
          // questions, and the submit gate enforces them server-side: carrying
          // on with an empty list hands the candidate a form they can fill
          // completely and still be refused for a question never shown.
          setJobLoadFailed(true);
        }

        console.error(
          "Failed to fetch job field config or details:",
          err
        );
      }
    })();
  }, [jobId]);

  const onSubmit = async (formData) => {
    if (!candidateId) return;

    // Screening-question gate (server also enforces). Block submit and mark
    // the unanswered required questions before anything is sent.
    const answers = form.methods.getValues("screening_answers") || {};
    const missing = (screeningQuestions || []).filter(
      (q) => q.is_required && !String(answers[q.id] ?? "").trim()
    );
    if (missing.length > 0) {
      missing.forEach((q) =>
        form.methods.setError(`screening_answers.${q.id}`, {
          type: "manual",
          message: "This question is required.",
        })
      );
      form.setStep(STEP_CONFIG.length - 1);
      toast.error("Please answer all required questions before submitting.");
      return;
    }
    // One definition, both candidate pages — and it sends the blank pairs.
    // See `utils/screeningAnswers.js`: an omitted pair means "leave the old
    // answer alone", so filtering the blanks out made clearing an optional
    // answer impossible.
    const question_answers = buildQuestionAnswers(screeningQuestions, answers);

    setIsFormSubmit(true);
    setSubmitError("");
    try {
      const response = await CandidateService.confirmCandidateDetail(candidateId, { ...formData, question_answers }, applicationId, hasConsented);
      // What the server actually did, not what the tenant setting said when
      // this page loaded — an admin flipping the toggle mid-session can't
      // desync us this way. Treat a missing flag as the two-factor default.
      const emailRequired = response?.email_confirmation_required !== false;
      // `email_masked` is null in single-factor mode — never interpolate it blind.
      const inboxes = [response?.email_masked, response?.alternate_email_masked].filter(Boolean).join(" and ");
      toast.success(
        emailRequired && inboxes
          ? `${response.message} (${inboxes})`
          : response?.message,
      );
      setSuccessData({
        message: response?.message,
        email: emailRequired ? response?.email_masked : null,
        alternateEmail: emailRequired ? response?.alternate_email_masked : null,
      emailConfirmationRequired: emailRequired,
      });
      setSubmissionSuccess(true);
      try {
        const bc = new BroadcastChannel(BC_CHANNEL);
        bc.postMessage({ type: "TABLE_REFRESH" });
        bc.close();
      } catch (_) { }

    } catch (err) {
      setSubmissionSuccess(false);
      const msg = err.response?.data?.message ?? err.response?.data?.error ?? "Submission failed. Please try again.";
      toast.error(msg);
      setSubmitError(msg);
    } finally {
      setIsFormSubmit(false);
    }
  };

  // `jobLanguagesRequired`, not `lockedLanguages`: the schema needs the LEVELS
  // the job named, and `requiredLanguageNames` strips them off. Handing it the
  // names made every job language demand a level, so a candidate applying to a
  // job that asked for "English, any level" was refused until they invented one.
  // The editor still locks by name — locking a row and demanding a level are
  // two different facts.
  const form = useReviewForm({ defaultValues, onSubmit, mandatoryFields, requiredLanguages: jobLanguagesRequired });

  // Steps to SHOW (form editable) — any backing section has arrived. null after
  // `complete` = all visible.
  const visibleStepKeys = useMemo(() => {
    if (extractionComplete) return null;
    return Object.entries(STEP_SECTIONS)
      .filter(([, secs]) => secs.length > 0 && secs.some((s) => readySections.includes(s)))
      .map(([k]) => k);
  }, [readySections, extractionComplete]);

  // Steps eligible for the green/red colour — ALL backing sections have arrived.
  // Until then the step stays amber even if it's already visible (e.g. summary
  // landed but personal-info fields are still extracting).
  const colorStepKeys = useMemo(() => {
    if (extractionComplete) return null;
    return Object.entries(STEP_SECTIONS)
      .filter(([, secs]) => secs.length > 0 && secs.every((s) => readySections.includes(s)))
      .map(([k]) => k);
  }, [readySections, extractionComplete]);

  // Backend sections whose response hasn't arrived yet — their fields stay
  // locked so the candidate can't edit data the LLM is about to overwrite.
  const lockedSections = useMemo(() => {
    if (extractionComplete) return [];
    return ALL_SECTIONS.filter((s) => !readySections.includes(s));
  }, [readySections, extractionComplete]);

  // Re-derive the form values from the accumulated draft, preserving any field
  // the candidate has already edited (keepDirtyValues).
  const repopulateForm = useCallback(() => {
    // The SSE draft never carries the job's required languages itself (that's
    // fetched separately, above) — mapApiToForm needs it on this key to prefill
    // a locked row per required language, the same way /candidate-review's
    // response already does for the confirm-details page.
    const mapped = mapApiToForm({ ...draftRef.current, job_required_languages: jobLanguagesRequired });
    // Auto-fill total experience from the (streamed) work entries when the
    // backend hasn't sent one yet, so the value is carried by the reset itself
    // and survives subsequent section resets instead of flickering to empty.
    if (!mapped.compensation.total_experience) {
      const calc = calculateTotalExperience(mapped.experience);
      if (calc !== "") mapped.compensation.total_experience = String(calc);
    }
    // keepDirtyValues preserves fields the candidate already edited (and keeps
    // them marked dirty) while filling/updating everything they haven't touched.
    form.methods.reset(mapped, { keepDirtyValues: true });
    // Re-run validation so step colours (amber→red/green) and the Submit gate
    // react the instant new data lands.
    form.methods.trigger();
  }, [form.methods, jobLanguagesRequired]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const resetExtractionState = () => {
    draftRef.current = { draft_data: {}, total_experience: undefined };
    consumedRef.current = 0;
    // Clear any edits/values from a previous resume so a fresh extraction starts
    // clean — otherwise repopulateForm's keepDirtyValues would preserve stale
    // fields from the prior upload.
    form.methods.reset(defaultValues);
    setIsExtracting(false);
    setReadySections([]);
    setExtractionComplete(false);
    setExtractionQuality("full");
    setCandidateId("");
    setApplicationId("");
    setSubmitError("");
    setSseError("");
    setSseProgress({ step: 0, total: 5, percentage: 0, message: "" });
  };

  const handleBackToUpload = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    resetExtractionState();
    setPhase("upload");
  };

  const handleBack = () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    navigate(`/user/job-detail/${jobId}`);
  };

  const handleCancel = () => {
    _cachedFile = null;
    _cachedConsent = false;
    _approvedFile = null;
    setSelectedFile(null);
    setHasConsented(false);
    resetUpload();
    resetExtractionState();
    setAlreadyApplied(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSseProgress = useCallback(
    (progressEvent) => {
      // `responseText` is the full cumulative stream on every callback. Only
      // parse the bytes we haven't consumed yet, up to the last newline — the
      // trailing partial line is left for the next callback.
      const resp = progressEvent.event.target.responseText;
      const fresh = resp.slice(consumedRef.current);
      const lastNL = fresh.lastIndexOf("\n");
      if (lastNL === -1) return; // no complete line yet
      consumedRef.current += lastNL + 1;
      const lines = fresh.slice(0, lastNL).split("\n");

      for (const line of lines) {
        const trimmed = line.trim();
        if (!trimmed.startsWith("data:")) continue;

        const jsonStr = trimmed.slice(5).trim();
        if (!jsonStr) continue;

        let event;
        try {
          event = JSON.parse(jsonStr);
        } catch {
          continue;
        }

        if (event.type === "progress") {
          setSseProgress({
            step: event.step ?? 0,
            total: event.total ?? 5,
            percentage: event.percentage ?? 0,
            message: event.message ?? "",
          });
        } else if (event.type === "section") {
          // First real section proves the candidate isn't a duplicate — now safe
          // to show the streaming review wizard.
          setIsExtracting(false);
          setPhase("review");
          // A section finished extracting — merge it and reveal/fill that step.
          if (event.data) {
            Object.assign(draftRef.current.draft_data, event.data);
            repopulateForm();
          }
          setReadySections((prev) => (prev.includes(event.section) ? prev : [...prev, event.section]));
        } else if (event.type === "complete") {
          // Authoritative payload — full draft, ids, total experience. Also flips
          // to review for the cached path, which sends no per-section events.
          setIsExtracting(false);
          setPhase("review");
          setCandidateId(event.candidate_id ?? "");
          setApplicationId(event.application_id ?? "");
          draftRef.current = {
            draft_data: event.draft_data ?? draftRef.current.draft_data,
            total_experience: event.total_experience,
          };
          repopulateForm();
          setExtractionQuality(isDeeplyEmpty(event.draft_data) ? "partial" : "full");
          setExtractionComplete(true);
          setSseProgress({
            step: event.total ?? 5,
            total: event.total ?? 5,
            percentage: 100,
            message: event.message ?? "Review your information",
          });
        } else if (event.type === "error") {
          const msg = event.message ?? "Something went wrong";
          setIsExtracting(false);
          if (msg.toLowerCase().includes("already applied")) {
            // Duplicate application — bounce back to the upload page with a
            // toast (the old behaviour) instead of rendering the error in the
            // review wizard, which can also happen via the post-extraction
            // check after sections have already streamed.
            setAlreadyApplied(true);
            setPhase("upload");
            toast.error(msg);
          } else {
            setSseError(msg);
          }
          return;
        }
      }
    },
    [repopulateForm],
  );

  const validateBeforeUpload = () => {
    if (!selectedFile) {
      toast.error("Please upload your resume");
      return false;
    }

    if (!jobId) {
      toast.error("Job ID is required");
      return false;
    }

    // Length validation
    if (selectedFile.name.length > 255) {
      toast.error("File name is too long");
      return false;
    }

    // Pattern validation
    const allowedExtensions = [".pdf", ".doc", ".docx"];
    const fileName = selectedFile.name.toLowerCase();

    if (!allowedExtensions.some((ext) => fileName.endsWith(ext))) {
      toast.error("Only PDF, DOC, and DOCX files are allowed");
      return false;
    }

    // Character restriction
    const invalidChars = /[<>:"/\\|?*\x00-\x1F]/;

    if (invalidChars.test(selectedFile.name)) {
      toast.error("File name contains invalid characters");
      return false;
    }

    // File size validation
    const maxSize = MAX_FILE_UPLOAD * 1024 * 1024;

    if (selectedFile.size > maxSize) {
      toast.error(`File size cannot exceed ${MAX_FILE_UPLOAD} MB`);
      return false;
    }

    return true;
  };

  const [validating, setValidating] = useState(false);
  const [validationModal, setValidationModal] = useState({ open: false, result: null });
  // The exact File instance already cleared by validation (passed, or the
  // recruiter chose Continue Anyway). If /apply fails (e.g. duplicate resume)
  // and the user retries with the SAME file, this lets us skip validating it
  // a second time — validation must not repeat once a decision has been made.

  const runExtraction = async () => {
    resetExtractionState();
    // Stay on the upload page until we know the candidate hasn't already
    // applied (handleSseProgress flips to "review" on the first section/complete).
    setIsExtracting(true);
    setSseProgress({ step: 0, total: 5, percentage: 0, message: "Starting…" });

    abortControllerRef.current = new AbortController();

    try {
      await CandidateService.extractResumeData(selectedFile, jobId, {
        // Resume extraction streams progress and can exceed the normal API timeout.
        timeout: 0,
        responseType: "text",
        signal: abortControllerRef.current.signal,
        onDownloadProgress: handleSseProgress,
      });
    } catch (error) {
      if (error.name === "CanceledError" || error.name === "AbortError" || error.code === "ERR_CANCELED") {
        return;
      }
      console.error("Apply API Error:", error);
      setSseError(error.message ?? "Failed to upload resume. Please try again.");
    } finally {
      setIsExtracting(false);
      abortControllerRef.current = null;
    }
  };

  const handleContinue = async () => {
    if (!validateBeforeUpload()) {
      return;
    }

    // Second guard behind the disabled button: a double-click or a repeated
    // Enter must not start two validate-then-extract runs.
    if (validating || isExtracting) return;

    // Already cleared this exact file (validated OK, or Continue Anyway was
    // chosen) — a retry after a failed /apply must not re-validate it.
    if (_approvedFile === selectedFile) {
      await runExtraction();
      return;
    }

    setValidating(true);
    try {
      const result = await CandidateService.validateResume(selectedFile, jobId);
      if (result?.is_valid === false) {
        setValidationModal({ open: true, result });
        return;
      }
      _approvedFile = selectedFile;
    } catch (error) {
      // Fail open — the pre-extraction check must never block the upload.
      console.error("Resume validation error:", error);
      _approvedFile = selectedFile;
    } finally {
      setValidating(false);
    }

    await runExtraction();
  };

  const handleValidationGoBack = () => {
    setValidationModal({ open: false, result: null });
    _approvedFile = null;
    handleCancel();
  };

  const handleValidationContinueAnyway = async () => {
    _approvedFile = selectedFile;
    setValidationModal({ open: false, result: null });
    await runExtraction();
  };

  if (portalUnavailable) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-20 font-sans text-center" style={{ zoom: 0.95 }}>
        <svg className="w-10 h-10 text-text-secondary mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          />
        </svg>
        <h1 className="text-text-primary font-bold text-xl leading-tight mb-1">Career portal unavailable</h1>
        <p className="text-text-secondary text-sm">This career portal is currently unavailable. Please check back later.</p>
      </div>
    );
  }

  if (jobLoadFailed) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-20 font-sans text-center" style={{ zoom: 0.95 }}>
        <svg className="w-10 h-10 text-text-secondary mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          />
        </svg>
        <h1 className="text-text-primary font-bold text-xl leading-tight mb-1">This job could not be loaded</h1>
        <p className="text-text-secondary text-sm mb-4">Something went wrong while loading this job. Please try again.</p>
        <Button onClick={() => window.location.reload()}>Try again</Button>
      </div>
    );
  }

  if (submissionSuccess) {
    return (
      <div className="min-h-[73vh] flex items-center justify-center px-4 my-2">
        <Card className="max-w-xl w-full p-5 text-center">
          <CheckCircle
            size={68}
            className="mx-auto text-green-500 mb-4"
          />

          <h1 className="text-2xl font-semibold text-text-primary mb-3">
            {successData?.emailConfirmationRequired
              ? "Check Your Email to Complete Your Application"
              : "Application Submitted"}
          </h1>

          <p className="text-text-secondary mb-4">
            {successData?.emailConfirmationRequired
              ? "We have sent a confirmation email to complete your application."
              : successData?.message || "Your application has been submitted. We will review it shortly."}
          </p>

          {successData?.email && (
            <div className="bg-background-primary border border-border-primary rounded-lg px-4 py-2 mb-4">
              <p className="text-xs text-text-secondary uppercase tracking-wide mb-1">
                Confirmation Email Sent To
              </p>

              <p className="text-text-primary font-medium">
                {successData.email}
                {successData.alternateEmail ? ` and ${successData.alternateEmail}` : ""}
              </p>
            </div>
          )}

          <Button
            onClick={() => navigate("/user/job-listing")}
            className="bg-button-secondary text-text-base"
          >
            Back to Jobs
          </Button>
        </Card>
      </div>
    );
  }

  // ── Review phase: inline streaming wizard ──────────────────────────────────
  if (phase === "review") {
    return (
      <div className="flex flex-col px-4 pb-4 sm:px-6 sm:pb-6 md:px-8 md:pb-8 lg:px-10 lg:pb-5 xl:px-12">
        <div className="w-full max-w-5xl xl:max-w-6xl mx-auto">
          {sseError ? (
            <div className="mt-6">
              <p className="text-red-400 text-sm mb-4">{sseError}</p>
              <Button
                onClick={alreadyApplied ? handleBack : handleBackToUpload}
                className="text-text-primary bg-background-secondary text-xs border border-border-primary rounded-lg flex items-center gap-1.5"
              >
                <ArrowLeft size={12} />
                {alreadyApplied ? "Back to job" : "Try again"}
              </Button>
            </div>
          ) : (
            <ReviewWizard
              form={form}
              mandatoryFields={mandatoryFields}
              extractionQuality={extractionQuality}
              readyStepKeys={visibleStepKeys}
              colorStepKeys={colorStepKeys}
              submitDisabled={!extractionComplete || !candidateId}
              isFormSubmit={isFormSubmit}
              submitError={submitError}
              currencySign={currencySign}
              languagesSlot={(disabled, required) => (
                <LanguageProficiencyRows
                  name="skills.languages"
                  disabled={disabled}
                  emptyText="No languages added yet."
                  lockedLanguages={lockedLanguages}
                  // Whether this job demands a language, derived once by
                  // StepSkills from the same mandatory_fields it marks its own
                  // label with.
                  languageRequired={required?.language}
                  // The job's rows: only a language it levelled is marked for
                  // a level, the one row the schema refuses blank.
                  levelledLanguages={jobLanguagesRequired}
                />
              )}
              screeningQuestions={screeningQuestions}
              onBack={handleBackToUpload}
              lockedSections={lockedSections}
              pendingNode={<ProgressStatus message={sseProgress.message} percentage={sseProgress.percentage} />}
              subtitle="We're extracting your resume. Review and correct each section as it appears, then submit."
            />
          )}
        </div>
      </div>
    );
  }



  // ── Upload phase: file + consent ───────────────────────────────────────────
  return (
    <div className="px-6 sm:px-10">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <div className="mb-2 mt-3 flex gap-3 items-center">
          <Button onClick={handleBack} className="text-text-primary bg-background-secondary text-xs border border-border-primary rounded-lg flex items-center gap-1.5">
            <ArrowLeft size={12} />
            Back
          </Button>
        </div>

        <div className="mb-3">
          <h1 className="text-text-primary font-semibold mb-1">
            Applying for {jobTitle}
          </h1>
        </div>

        <Card className="bg-background-secondary border-0 md:border md:border-border-primary md:p-4 p-0 rounded-md">
          <CardHeader className="p-0 items-start">
            <h1 className="text-text-primary font-semibold text-base mb-1">Resume</h1>
            <p className="text-text-secondary md:text-[12px] text-xs mb-3 leading-relaxed">
              Your name, email, phone and other details will be automatically extracted from your resume. You can review and correct each section as it appears
            </p>
          </CardHeader>

          {/* File Upload Section */}
          <div
            className={cn(
              "border border-dashed rounded-lg p-3 text-center bg-background-primary transition-all duration-200",
              !hasConsented
                ? "border-gray-700 opacity-50 cursor-not-allowed"
                : isDragging
                  ? "border-blue-500 bg-blue-500/5 cursor-pointer"
                  : "border-gray-600 hover:border-gray-500 cursor-pointer",
            )}
            onDragEnter={hasConsented ? handleDragEnter : undefined}
            onDragLeave={hasConsented ? handleDragLeave : undefined}
            onDragOver={hasConsented ? handleDragOver : undefined}
            onDrop={hasConsented ? handleDrop : undefined}
            onClick={hasConsented ? triggerFileInput : undefined}
          >
            <Input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx"
              disabled={!hasConsented || isExtracting}
              onChange={(e) => {
                const file = e.target.files?.[0];

                if (!file) return;

                if (file.name.length > 255) {
                  toast.error("File name is too long");
                  return;
                }

                const invalidChars = /[<>:"/\\|?*\x00-\x1F]/;

                if (invalidChars.test(file.name)) {
                  toast.error("File name contains invalid characters");
                  return;
                }

                handleFileSelect(file);
              }}
            />

            {/* Progress Indicator */}
            <div className="flex justify-center mb-2 text-text-primary">
              {isExtracting || validating ? (
                <div className="loader mt-6 "></div>
              ) : (
                <div className="w-8 h-8 rounded-full bg-background-secondary border border-border-primary flex items-center justify-center">
                  <Upload className="w-4 h-4" />
                </div>
              )}
            </div>

            {/* Instructions. The only real progress here is the extraction
                stream's — picking a file sends nothing, and the old
                "Uploading your resume… 40%" was a 2-second animation over an
                idle page. */}
            {isExtracting ? (
              <ProgressStatus message={sseProgress.message} percentage={sseProgress.percentage} />
            ) : validating ? (
              <p className="text-text-primary text-xs font-medium">Checking your resume...</p>
            ) : (
              <>
                <p className="text-text-primary text-xs">Drop your resume or click to browse</p>
                <Button
                  className="text-text-blue text-xs hover:text-text-blue p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerFileInput();
                  }}
                >
                  Click to browse
                </Button>
              </>
            )}
          </div>

          {/* Supported File Types and Size */}
          <div className="flex justify-between mt-3 mb-2 text-[10px] text-text-secondary">
            <span>Supported: PDF, DOC, and DOCX</span>
            <span>Maximum Size: {MAX_FILE_UPLOAD} MB</span>
          </div>

          <label
            htmlFor="ai-consent"
            className={cn(
              "flex items-start gap-3 mb-3 p-3 rounded-lg border transition-colors",
              isExtracting || selectedFile ? "cursor-not-allowed opacity-60" : "cursor-pointer",
              hasConsented ? "border-blue-500/50 bg-blue-500/5" : "border-border-primary hover:border-gray-500",
            )}
          >
            <div className="relative flex-shrink-0 mt-0.5">
              {/* `Boolean(selectedFile)`, not the File itself: `disabled` takes
                  a boolean, and React warns about anything else. */}
              <Input
                type="checkbox"
                id="ai-consent"
                disabled={Boolean(selectedFile) || isExtracting}
                checked={hasConsented}
                onChange={(e) => !isExtracting && setHasConsented(e.target.checked)}
                className="peer sr-only"
              />
              <div
                className={cn(
                  "w-4 h-4 rounded border-2 flex items-center justify-center transition-colors",
                  hasConsented ? "bg-[#3580d7] border-[#3580d7]" : "bg-transparent border-gray-500",
                )}
              >
                {hasConsented && <Check size={10} strokeWidth={3} className="text-white" />}
              </div>
            </div>

            <div className="flex flex-col gap-0.5">
              <span className="text-text-primary text-[12px] leading-snug">
                {consentText}
              </span>
              {applyEmailConsentRequired !== null && (
                <span className="text-text-secondary text-[11px] leading-snug mt-1">
                  {applyEmailConsentRequired
                    ? "After you review your details, we'll email you a confirmation link — your application is complete once you click it."
                    : "Your application is submitted as soon as you review your details and continue — no confirmation email is needed."}
                </span>
              )}
            </div>
          </label>

          {/* Selected File Preview */}
          {selectedFile && (
            <div className="flex items-center gap-3 rounded-lg px-3 py-3 mt-2 border border-border-primary">
              <div className="flex-shrink-0">
                <FilePreview file={selectedFile} />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-text-primary text-sm font-medium truncate">{selectedFile.name}</p>
                <p className="text-text-secondary text-xs mt-0.5">
                  {selectedFile.size < 1024 * 1024 ? `${(selectedFile.size / 1024).toFixed(1)} KB` : `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`}
                </p>
              </div>

              <Button
                onClick={handleCancel}
                disabled={!selectedFile || isExtracting || validating}
                className="text-text-secondary transition-colors p-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-text-secondary"
              >
                <X size={18} />
              </Button>
            </div>
          )}

          {/* Error (e.g. unreadable resume) — shown inline while still on the
              upload page; "already applied" is surfaced via a toast instead. */}
          {sseError && <p className="mt-3 text-red-400 text-xs">{sseError}</p>}
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 mt-3 mb-3">
          <Button
            onClick={handleContinue}
            disabled={!selectedFile || !hasConsented || isExtracting || validating}
            className="bg-button-secondary text-text-base text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {validating ? "Checking resume…" : "Continue"}
            <ArrowRight />
          </Button>
        </div>
      </div>

      <ResumeValidationModal
        open={validationModal.open}
        fileName={selectedFile?.name}
        validation={validationModal.result}
        mode="individual"
        onGoBack={handleValidationGoBack}
        onContinueAnyway={handleValidationContinueAnyway}
      />
    </div>
  );
};

export default UserApply;
